/**
 * @typedef {import("./Globe").Globe} Globe
 * @typedef {import("maplibre-gl").Map} GLMap
 * @typedef {import("./CameraSynchronizer").default CameraSynchronizer}
 */

export default class MapGlobeSwapper extends EventTarget {
    /**
     * @param {Globe} globe
     * @param {GLMap} map
     * @param {string} fadeOutClass - class name that will cause a fade in animation when appended
     * @param {string} fadeInClass - class name that will cause a fade out animation when appended
     * @param {CameraSynchronizer} cameraSynchronizer
     */
    constructor(globe, map, fadeOutClass, fadeInClass, cameraSynchronizer) {
        super()
        /**
         * @type Globe
         */
        this.globe = globe
        /** @type GLMap */
        this.map = map
        /**
         * @type string
         */
        this.fadeOutClass = fadeOutClass
        /**
         * @type string
         */
        this.fadeInClass = fadeInClass
        /**
         * @type boolean
         */
        this.mapVisible = true
        /**
         * @type boolean
         */
        this.globeVisible = true
        /**
         * @type HTMLCanvasElement
         */
        this.globeCanvas = globe.getCanvas()
        /**
         * @type HTMLCanvasElement
         */
        this.mapCanvas = map.getCanvas()

        /**
         * @type CameraSynchronizer
         */
        this.cameraSynchronizer = cameraSynchronizer

        this.onGlobeAnimationEnd = function (event) {
            // Short circuit on any animation that isn't fade out
            if (event.animationName !== this.fadeOutClass) {
                return
            }

            this.globeVisible = false
        }.bind(this)

        // Use the animationend event to tell when the globe has become fully invisible
        this.globeCanvas.addEventListener(
            "animationend",
            this.onGlobeAnimationEnd
        )
    }

    swapToMap() {
        this.globe.fadeOutRing()
        this.globe.fadeOutTapHelpers()
        this.globeCanvas.classList.remove(this.fadeInClass)
        this.globeCanvas.classList.add(this.fadeOutClass)
        this.globeCanvas.style.pointerEvents = "none"
        this.cameraSynchronizer.master = "map"
    }

    swapToMapImmediately() {
        this.globeCanvas.classList.remove(this.fadeInClass)
        this.globeCanvas.classList.add(this.fadeOutClass)
        this.globeCanvas.style.visibility = "hidden"
        this.globeVisible = false
        this.globeCanvas.style.pointerEvents = "none"
        this.cameraSynchronizer.master = "map"
    }

    swapToGlobe() {
        this.globe.fadeInRing()
        this.globe.fadeInTapHelpers()
        this.globeVisible = true
        this.globeCanvas.classList.add(this.fadeInClass)
        this.globeCanvas.classList.remove(this.fadeOutClass)
        this.globeCanvas.style.removeProperty('visibility')
        this.globeCanvas.style.pointerEvents = "auto"
        this.cameraSynchronizer.master = "globe"

        // Unbound the map
        this.map.setMaxBounds()
        // this.cameraSynchronizer.syncGlobe()
    }

    /**
     * Remove event listeners and other associated data so that the object can be cleanly disposed of
     */
    cleanup() {
        this.globeCanvas.removeEventListener(
            "animationend",
            this.onGlobeAnimationEnd
        )
    }
}
